import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Calculator as CalcIcon, AlertTriangle, Info, ArrowRight, Plus, Trash2,
  Scale as ScaleIcon, CheckCircle2, HelpCircle, FileText, Upload, Gavel, ShieldCheck,
} from "lucide-react";
import { api, formatApiError, formatZAR } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import CampaignBanner from "@/components/CampaignBanner";
import CalculatorSwitcher from "@/components/CalculatorSwitcher";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// MaintenanceCalculator (refined Phase 9.1)
//
// Adds:
//   * Input clarity (after-tax labels + helper text + global note)
//   * Smart output (range + "How this was calculated")
//   * Agreement-vs-dispute decision block (yes/not-sure/no)
//   * Readiness indicator
//   * "What happens next" flow
//   * Branching to proforma with agreement_intent in metadata
// ---------------------------------------------------------------------------

function emptyChild() {
  return { name: "", age: "", school_fees: "", food: "", medical: "", transport: "", extramurals: "", clothing: "", other_costs: "" };
}

const HELPER = {
  income:   "After tax & deductions — your monthly take-home pay (i.e. the amount paid into your bank account).",
  expenses: "Your own living costs — rent/bond, food, transport, insurance, debt, utilities, etc. Exclude amounts spent directly on the children (those go in the children's section below).",
  child:    "Direct monthly costs for each child — school fees, food, medical, transport, extramurals. Use the lifestyle benchmark if exact amounts are unknown.",
};

export default function MaintenanceCalculator() {
  const navigate = useNavigate();
  const [partyA, setPartyA] = useState({ name: "", role: "father", monthly_income: "", monthly_expenses: "" });
  const [partyB, setPartyB] = useState({ name: "", role: "mother", monthly_income: "", monthly_expenses: "" });
  const [children, setChildren] = useState([emptyChild()]);
  const [lifestyle, setLifestyle] = useState("standard");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreementIntent, setAgreementIntent] = useState(null); // 'yes' | 'not_sure' | 'no'

  // Lead-capture
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [proformaLoading, setProformaLoading] = useState(false);

  const updateChild = (idx, field, value) => {
    setChildren((curr) => curr.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };
  const addChild = () => setChildren((c) => [...c, emptyChild()]);
  const removeChild = (idx) => setChildren((c) => (c.length <= 1 ? c : c.filter((_, i) => i !== idx)));

  const handleCalc = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        party_a: {
          ...partyA,
          monthly_income: Number(partyA.monthly_income) || 0,
          monthly_expenses: Number(partyA.monthly_expenses) || 0,
        },
        party_b: {
          ...partyB,
          monthly_income: Number(partyB.monthly_income) || 0,
          monthly_expenses: Number(partyB.monthly_expenses) || 0,
        },
        children: children
          .filter((c) => c.name || c.age || c.school_fees || c.food || c.medical || c.transport || c.extramurals || c.clothing)
          .map((c) => ({
            name: c.name,
            age: Number(c.age) || null,
            school_fees: Number(c.school_fees) || 0,
            food: Number(c.food) || 0,
            medical: Number(c.medical) || 0,
            transport: Number(c.transport) || 0,
            extramurals: Number(c.extramurals) || 0,
            clothing: Number(c.clothing) || 0,
            other_costs: Number(c.other_costs) || 0,
          })),
        lifestyle,
      };
      const { data } = await api.post("/calc/maintenance", payload);
      setResult(data);
      window.requestAnimationFrame(() => {
        document.getElementById("maintenance-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!agreementIntent) {
      toast.error("Please tell us whether both parties are likely to agree.");
      return;
    }
    if (!contactName.trim() || !contactEmail.trim()) {
      toast.error("Please add your name and email to continue.");
      return;
    }
    setProformaLoading(true);
    try {
      const { data } = await api.post("/proforma/issue", {
        name: contactName.trim(),
        email: contactEmail.trim().toLowerCase(),
        phone: contactPhone.trim() || null,
        matter_type: "maintenance_divorce",
        pricing_mode: "managed",
        metadata: {
          agreement_intent: agreementIntent,
          indicative_payment_monthly: result?.indicative_payment_monthly,
          paying_party: result?.paying_party,
          lifestyle: result?.lifestyle,
        },
      });
      toast.success(
        agreementIntent === "yes"
          ? "Pro-forma issued — next we'll set up the mutual agreement."
          : agreementIntent === "no"
          ? "Pro-forma issued — next we'll prepare the J941 dispute application."
          : "Pro-forma issued — let's complete the full disclosure."
      );
      navigate(`/proforma/${data.proforma_id || data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProformaLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <SiteHeader />
      <CampaignBanner kicker="EH Inc · Maintenance Calculator" />
      <CalculatorSwitcher active="maintenance" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        {/* Hero */}
        <header className="mb-10 max-w-3xl" data-testid="maintenance-calc-hero">
          <div className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-amber-700">
            <ScaleIcon className="h-3.5 w-3.5" /> Maintenance & Divorce Support
          </div>
          <h1 className="mt-3 font-serif text-5xl lg:text-6xl leading-[1.05] tracking-tight text-zinc-900">
            What's a fair contribution toward your children?
          </h1>
          <p className="mt-5 text-zinc-600 leading-relaxed">
            A guided, proportional estimate calibrated to South African legal practice. We'll then prepare either a mutual agreement, full disclosure, or a J941 court application — depending on your situation.
          </p>
          <div className="mt-3 inline-block text-sm text-amber-800 bg-amber-50 border border-amber-200 px-4 py-2.5" data-testid="maintenance-agreement-note">
            <span className="font-medium">If both parties agree, the process is faster and simpler</span> — we prepare everything needed for court approval without unnecessary disputes.
          </div>
          <div className="mt-2 text-xs text-zinc-500 inline-flex items-center gap-1.5" data-testid="maintenance-global-note">
            <Info className="h-3.5 w-3.5" /> All amounts must be monthly <strong>net</strong> (after tax) values.
          </div>
        </header>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Calculator column */}
          <form className="lg:col-span-6 bg-white border border-zinc-200 p-8 lg:p-10 space-y-8" onSubmit={handleCalc} data-testid="maintenance-calc-form">

            <Section title="Income — monthly net (after tax / take-home pay)"
                       helper={HELPER.income}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Your name"           value={partyA.name}              onChange={(v) => setPartyA({ ...partyA, name: v })} placeholder="John" testid="party-a-name" />
                <Field label="Other party's name"  value={partyB.name}              onChange={(v) => setPartyB({ ...partyB, name: v })} placeholder="Jane" testid="party-b-name" />
                <ZARField label="Your monthly take-home pay"           value={partyA.monthly_income}   onChange={(v) => setPartyA({ ...partyA, monthly_income: v })}   testid="party-a-income" />
                <ZARField label="Other party's monthly take-home pay"  value={partyB.monthly_income}   onChange={(v) => setPartyB({ ...partyB, monthly_income: v })}   testid="party-b-income" />
              </div>
            </Section>

            <Section title="Personal monthly expenses (excluding child costs)"
                       helper={HELPER.expenses}>
              <div className="grid grid-cols-2 gap-4">
                <ZARField label="Your personal expenses"          value={partyA.monthly_expenses} onChange={(v) => setPartyA({ ...partyA, monthly_expenses: v })} testid="party-a-expenses" />
                <ZARField label="Other party's personal expenses" value={partyB.monthly_expenses} onChange={(v) => setPartyB({ ...partyB, monthly_expenses: v })} testid="party-b-expenses" />
              </div>
            </Section>

            <Section title="Children's monthly expenses (direct costs only)"
                       helper={HELPER.child}>
              {children.map((c, idx) => (
                <div key={idx} className="border border-zinc-200 p-4 bg-zinc-50/40 mb-3" data-testid={`child-row-${idx}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs tracking-[0.18em] uppercase text-zinc-500">Child {idx + 1}</div>
                    {children.length > 1 && (
                      <button type="button" onClick={() => removeChild(idx)} className="text-zinc-400 hover:text-red-500" data-testid={`remove-child-${idx}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Name" value={c.name} onChange={(v) => updateChild(idx, "name", v)} placeholder="Lucy" testid={`child-${idx}-name`} />
                    <Field label="Age"  value={c.age}  onChange={(v) => updateChild(idx, "age", v)}  placeholder="8" type="number" testid={`child-${idx}-age`} />
                    <ZARField label="School fees"   value={c.school_fees} onChange={(v) => updateChild(idx, "school_fees", v)} testid={`child-${idx}-school`} />
                    <ZARField label="Food"          value={c.food}        onChange={(v) => updateChild(idx, "food", v)}        testid={`child-${idx}-food`} />
                    <ZARField label="Medical"       value={c.medical}     onChange={(v) => updateChild(idx, "medical", v)}     testid={`child-${idx}-medical`} />
                    <ZARField label="Transport"     value={c.transport}   onChange={(v) => updateChild(idx, "transport", v)}   testid={`child-${idx}-transport`} />
                    <ZARField label="Extramurals"   value={c.extramurals} onChange={(v) => updateChild(idx, "extramurals", v)} testid={`child-${idx}-extramurals`} />
                    <ZARField label="Clothing"      value={c.clothing}    onChange={(v) => updateChild(idx, "clothing", v)}    testid={`child-${idx}-clothing`} />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addChild} className="text-sm font-medium text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1.5" data-testid="add-child-btn">
                <Plus className="h-4 w-4" /> Add another child
              </button>
            </Section>

            <Section title="Children's standard of living"
                       helper="Sets the cost basis when exact amounts above are missing.">
              <div className="grid grid-cols-3 gap-2">
                {["basic", "standard", "premium"].map((l) => (
                  <button type="button" key={l}
                            onClick={() => setLifestyle(l)}
                            data-testid={`lifestyle-${l}`}
                            className={`px-3 py-2.5 text-sm border transition-colors ${lifestyle === l ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"}`}>
                    {l[0].toUpperCase() + l.slice(1)}
                  </button>
                ))}
              </div>
            </Section>

            {error && <div className="text-sm text-red-600" data-testid="maintenance-calc-error">{error}</div>}

            <button type="submit" disabled={loading}
                       className="w-full bg-zinc-900 text-white px-6 py-4 font-medium hover:bg-zinc-700 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                       data-testid="maintenance-calc-submit">
              <CalcIcon className="h-4 w-4" /> {loading ? "Calculating…" : "Calculate indicative maintenance →"}
            </button>
          </form>

          {/* Result column */}
          <div className="lg:col-span-6 space-y-6" id="maintenance-result">
            {!result ? (
              <div className="h-full min-h-[520px] border border-zinc-200 bg-white p-10 lg:p-12 grid place-items-center text-center">
                <div>
                  <Sparkles className="h-8 w-8 text-zinc-300 mx-auto" />
                  <p className="mt-4 text-zinc-500 max-w-sm mx-auto leading-relaxed">
                    Enter both parties' net income and the children's costs to see a guided assessment.
                  </p>
                </div>
              </div>
            ) : (
              <ResultColumn
                result={result}
                agreementIntent={agreementIntent} setAgreementIntent={setAgreementIntent}
                contactName={contactName} setContactName={setContactName}
                contactEmail={contactEmail} setContactEmail={setContactEmail}
                contactPhone={contactPhone} setContactPhone={setContactPhone}
                onContinue={handleContinue}
                proformaLoading={proformaLoading}
              />
            )}
          </div>
        </div>

        <p className="mt-12 text-xs text-zinc-500 max-w-2xl leading-relaxed" data-testid="maintenance-disclaimer-footer">
          This is an indicative assessment — court discretion applies. Maintenance orders are made by the Maintenance Court (Maintenance Act 99 of 1998) and may differ based on the parties' full disclosure, the children's reasonable needs and other circumstances the court considers relevant.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Result Column — wraps all output panels
// ---------------------------------------------------------------------------

function ResultColumn({ result, agreementIntent, setAgreementIntent, contactName, setContactName, contactEmail, setContactEmail, contactPhone, setContactPhone, onContinue, proformaLoading }) {
  const intentCtaLabel = useMemo(() => {
    if (agreementIntent === "yes")     return "Continue to Mutual Agreement";
    if (agreementIntent === "no")      return "Continue to J941 Court Application";
    if (agreementIntent === "not_sure") return "Continue to Full Disclosure";
    return "Continue";
  }, [agreementIntent]);

  return (
    <>
      <HeadlinePanel result={result} />
      <NarrativePanel narrative={result.narrative} />
      <ReadinessPanel readiness={result.readiness} />
      <EligibilityPanel flags={result.eligibility_flags} />
      <AgreementPathPanel intent={agreementIntent} onChange={setAgreementIntent} />
      <NextStepsPanel intent={agreementIntent} />

      <div className="border border-zinc-200 bg-white p-6 lg:p-8" data-testid="maintenance-continue-panel">
        <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase">Continue</div>
        <div className="text-sm text-zinc-700 mt-1 mb-4 leading-relaxed">
          We'll prepare a pro-forma reflecting your chosen path. {intentCtaLabel === "Continue to Mutual Agreement" && "Then you and the other party can confirm the terms online."}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <input type="text"  value={contactName}  onChange={(e) => setContactName(e.target.value)}  placeholder="Your name" data-testid="maintenance-contact-name"  className="border border-zinc-300 px-3 py-2 text-sm bg-white" />
          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="Email"     data-testid="maintenance-contact-email" className="border border-zinc-300 px-3 py-2 text-sm bg-white" />
          <input type="tel"   value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Mobile (optional)" data-testid="maintenance-contact-phone" className="border border-zinc-300 px-3 py-2 text-sm bg-white" />
        </div>
        <button type="button" onClick={onContinue} disabled={proformaLoading || !agreementIntent}
                   className="w-full bg-zinc-900 text-white px-5 py-3.5 text-sm font-medium hover:bg-zinc-700 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-40"
                   data-testid="maintenance-continue-cta">
          {proformaLoading ? "Issuing pro-forma…" : intentCtaLabel} <ArrowRight className="h-4 w-4" />
        </button>
        {!agreementIntent && (
          <div className="text-xs text-amber-700 mt-2">Select an agreement path above first.</div>
        )}
      </div>
    </>
  );
}


// ---------------------------------------------------------------------------
// Sub-panels
// ---------------------------------------------------------------------------

function HeadlinePanel({ result }) {
  const a = result.parties.a;
  const b = result.parties.b;
  const paying = result.paying_party === "a" ? a : b;
  const receiving = result.paying_party === "a" ? b : a;
  return (
    <div className="border border-zinc-200 bg-white" data-testid="maintenance-result-panel">
      {/* Headline */}
      <div className="p-8 lg:p-10 bg-zinc-900 text-white">
        <div className="text-overline text-zinc-400 text-xs tracking-[0.18em] uppercase">Suggested monthly contribution</div>
        <div className="mt-3 font-serif text-5xl lg:text-6xl font-medium leading-none tracking-tight" data-testid="maintenance-indicative-amount">
          {formatZAR(result.indicative_payment_monthly)} <span className="text-2xl text-zinc-400 ml-2">/ month</span>
        </div>
        <div className="mt-2 text-sm text-zinc-300" data-testid="maintenance-range">
          Typical range: <span className="font-medium text-white">{formatZAR(result.range.low)} – {formatZAR(result.range.high)}</span> · ±{result.range.band_pct}%
        </div>
        <div className="mt-3 text-sm text-zinc-300 leading-relaxed">
          {paying.name} would contribute toward {receiving.name}'s care of the children, based on a {paying.share_pct}% share of combined income.
        </div>
      </div>
      {/* Party breakdown */}
      <div className="grid grid-cols-2 divide-x divide-zinc-200">
        <div className="p-6">
          <div className="text-xs text-zinc-500 uppercase tracking-[0.18em]">{a.name || "Party A"}</div>
          <div className="mt-1 font-serif text-2xl text-zinc-900" data-testid="result-a-pct">{a.share_pct}%</div>
          <div className="text-xs text-zinc-500 mt-1">share · {formatZAR(a.contribution)} / month</div>
        </div>
        <div className="p-6">
          <div className="text-xs text-zinc-500 uppercase tracking-[0.18em]">{b.name || "Party B"}</div>
          <div className="mt-1 font-serif text-2xl text-zinc-900" data-testid="result-b-pct">{b.share_pct}%</div>
          <div className="text-xs text-zinc-500 mt-1">share · {formatZAR(b.contribution)} / month</div>
        </div>
      </div>
      <div className="p-5 border-t border-zinc-200 bg-amber-50/40 text-sm text-amber-900" data-testid="maintenance-disclaimer-inline">
        <div className="font-medium">{result.disclaimer}</div>
        <div className="text-xs text-amber-700 mt-1">{result.court_order_note}</div>
      </div>
    </div>
  );
}

function NarrativePanel({ narrative }) {
  if (!narrative?.length) return null;
  return (
    <div className="border border-zinc-200 bg-white p-6" data-testid="maintenance-narrative-panel">
      <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase mb-3">How this was calculated</div>
      <div className="space-y-3">
        {narrative.map((n) => (
          <div key={n.id} className="text-sm" data-testid={`narrative-${n.id}`}>
            <div className="flex justify-between gap-3">
              <span className="text-zinc-600">{n.label}</span>
              <span className="font-medium text-zinc-900 text-right">{n.value}</span>
            </div>
            {n.detail && <div className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{n.detail}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReadinessPanel({ readiness }) {
  if (!readiness) return null;
  const pct = readiness.pct;
  const barColor = pct >= 100 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="border border-zinc-200 bg-white p-6" data-testid="maintenance-readiness-panel">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase">Assessment completeness</div>
        <div className="font-serif text-xl text-zinc-900" data-testid="readiness-pct">{pct}%</div>
      </div>
      <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden mb-3">
        <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs text-zinc-600 mb-3">{readiness.headline}</div>
      <ul className="space-y-1 text-xs">
        {readiness.items.map((it) => (
          <li key={it.id} className="flex items-start gap-2" data-testid={`readiness-item-${it.id}`}>
            {it.satisfied
              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
              : <span className={`h-3.5 w-3.5 rounded-full border flex-shrink-0 mt-0.5 ${it.severity === "Critical" ? "border-red-500" : "border-zinc-300"}`} />
            }
            <div className="flex-1">
              <div className={it.satisfied ? "text-zinc-500 line-through" : "text-zinc-800"}>{it.label}</div>
              {!it.satisfied && <div className="text-zinc-500">{it.improves}</div>}
            </div>
            <span className={`text-[10px] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded ${it.severity === "Critical" ? "bg-red-50 text-red-700" : it.severity === "Important" ? "bg-amber-50 text-amber-700" : "bg-zinc-50 text-zinc-500"}`}>{it.severity}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EligibilityPanel({ flags }) {
  if (!flags?.length) return null;
  return (
    <div className="border border-zinc-200 bg-white p-6" data-testid="maintenance-eligibility-flags">
      <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase mb-3">Notes & flags</div>
      <ul className="space-y-2">
        {flags.map((f) => (
          <li key={f.id} className={`flex items-start gap-2 text-sm ${f.level === "warning" ? "text-amber-800" : "text-zinc-700"}`} data-testid={`eligibility-${f.id}`}>
            {f.level === "warning" ? <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" /> : <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />}
            <div>
              <div className="font-medium">{f.label}</div>
              {f.detail && <div className="text-xs text-zinc-500">{f.detail}</div>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AgreementPathPanel({ intent, onChange }) {
  const OPTIONS = [
    { id: "yes",      label: "Yes",       sub: "We're aligned on what's fair",       icon: <ShieldCheck className="h-4 w-4" />, accent: "border-emerald-300 bg-emerald-50/40 text-emerald-900" },
    { id: "not_sure", label: "Not sure",  sub: "We need to discuss / disclose more", icon: <HelpCircle className="h-4 w-4" />,  accent: "border-amber-300 bg-amber-50/40 text-amber-900" },
    { id: "no",       label: "No",         sub: "We're heading to court",            icon: <Gavel className="h-4 w-4" />,       accent: "border-red-300 bg-red-50/40 text-red-900" },
  ];
  return (
    <div className="border border-zinc-200 bg-white p-6" data-testid="maintenance-agreement-path">
      <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase mb-1">Decision · the next step</div>
      <div className="font-serif text-xl text-zinc-900 mb-3">Are both parties likely to agree?</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {OPTIONS.map((o) => (
          <button key={o.id} type="button"
                    onClick={() => onChange(o.id)}
                    data-testid={`agreement-intent-${o.id}`}
                    className={`text-left border p-3 transition-colors ${intent === o.id ? "border-zinc-900 ring-2 ring-zinc-900" : "border-zinc-300 hover:border-zinc-500"} ${o.accent}`}>
            <div className="flex items-center gap-1.5 text-sm font-medium">{o.icon} {o.label}</div>
            <div className="text-xs text-zinc-600 mt-1">{o.sub}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function NextStepsPanel({ intent }) {
  const STEPS = [
    { id: 1, label: "Complete financial disclosure", icon: <FileText className="h-3.5 w-3.5" /> },
    { id: 2, label: "Upload supporting documents",   icon: <Upload className="h-3.5 w-3.5" /> },
    { id: 3, label: "Lawyer reviews disclosure",     icon: <ScaleIcon className="h-3.5 w-3.5" /> },
    {
      id: 4,
      label:
        intent === "yes"
          ? "Both parties confirm the Maintenance Agreement"
          : intent === "no"
          ? "J941 court application filed"
          : "Mutual agreement OR J941 — depending on the disclosure outcome",
      icon: <Gavel className="h-3.5 w-3.5" />,
    },
  ];
  return (
    <div className="border border-zinc-200 bg-white p-6" data-testid="maintenance-next-steps">
      <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase mb-3">What happens next</div>
      <ol className="space-y-2">
        {STEPS.map((s) => (
          <li key={s.id} className="flex items-center gap-3 text-sm text-zinc-800" data-testid={`next-step-${s.id}`}>
            <span className="flex-shrink-0 h-6 w-6 rounded-full bg-zinc-100 text-xs flex items-center justify-center text-zinc-600">{s.id}</span>
            <span className="text-zinc-400">{s.icon}</span>
            <span>{s.label}</span>
          </li>
        ))}
      </ol>
      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 mt-3" data-testid="next-steps-court-order-note">
        Whichever path we take, the final outcome must be made an <strong>order of court</strong> to be enforceable.
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Form primitives
// ---------------------------------------------------------------------------

function Section({ title, helper, children }) {
  return (
    <div>
      <div className="mb-3">
        <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase">{title}</div>
        {helper && <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{helper}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-zinc-500 mb-1">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
                data-testid={testid}
                className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none" />
    </label>
  );
}

function ZARField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-zinc-500 mb-1">{label}</span>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">R</span>
        <input type="text" inputMode="numeric"
                  value={value}
                  onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
                  data-testid={testid}
                  className="w-full pl-6 pr-2 py-2 border border-zinc-300 text-sm bg-white focus:border-zinc-900 focus:outline-none" />
      </div>
    </label>
  );
}
